// Custom JS for the CML / Pay Later checkout page. Managed by the checkout app.
//
// Everything here is scoped to #cml_checkout_start and does three things:
//   1. Reorders the shipping address fields into a Shopify-like flow.
//   2. Moves the step's primary button into the left-column footer, next to
//      "Return to cart", and makes both steps' buttons look the same.
//   3. Normalises the summary prices: whole units (the app drops the decimals
//      when creating the order anyway) plus the currency, which the app only
//      renders on line items and not on shipping/tax/total.
//
// The app uses two different primary buttons depending on the step:
//   - #place_order          (<input>, e.g. "Continue to shipping") in the form
//   - #checkout_place_order (<a> "Place Order") in the summary sidebar
// Only one is visible at a time; we relocate whichever is visible and never
// touch the hidden one (touching it produces duplicate buttons).
(function () {
  'use strict';

  var CONTAINER_SELECTOR = '#cml_checkout_start';
  var ACTIONS_CLASS = 'mg-checkout-actions';
  var FOOTER_CLASS = 'mg-checkout-footer';
  var BACK_LINK_TEXT = 'return to cart';
  var SHIPPING_LABEL = 'Estimated delivery cost';
  var PRIMARY_IDS = ['checkout_place_order', 'place_order'];
  var MAX_WAIT_MS = 60000; // keep re-applying across steps / summary re-renders

  // Summary amounts the app renders without a currency and with 2 decimals.
  var MONEY_SELECTORS = [
    '.shipping .price',
    '.shipping-row .price-row',
    '.tax .price',
    '.grand_total .price'
  ];

  var applying = false;

  function isVisible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  /* ---------------- price formatting ---------------- */

  // Pull a number out of a price string, coping with "15.00", "1 234,50",
  // "$15" and "15 kr".
  function parseAmount(text) {
    if (!text) return null;
    var m = text.replace(/ /g, ' ').match(/-?\d[\d\s.,]*/);
    if (!m) return null;
    var s = m[0].replace(/\s/g, '');
    var dec = Math.max(s.lastIndexOf(','), s.lastIndexOf('.'));
    if (dec > -1) {
      var decimals = s.length - dec - 1;
      // 1 or 2 trailing digits = decimal separator; otherwise a thousands mark.
      s = decimals === 1 || decimals === 2
        ? s.slice(0, dec).replace(/[.,]/g, '') + '.' + s.slice(dec + 1)
        : s.replace(/[.,]/g, '');
    }
    var n = parseFloat(s);
    return isNaN(n) ? null : n;
  }

  // Learn the currency format from a line item, which the app already renders
  // correctly (e.g. "$1.00" -> prefix "$", or "10,00 kr" -> suffix "kr").
  function detectCurrency(container) {
    var el = container.querySelector('.cml_line_item .price');
    if (!el) return null;
    var t = (el.textContent || '').replace(/ /g, ' ').trim();
    var m = t.match(/-?\d[\d\s.,]*/);
    if (!m) return null;
    // The line item may carry a second (BSS B2B) price, so bound the suffix to
    // the characters right after the first amount, up to the next digit.
    var after = t.slice(m.index + m[0].length).match(/^[^\d]*/);
    var prefix = t.slice(0, m.index).trim();
    var suffix = (after ? after[0] : '').trim();
    // With a prefix currency ("€550,00") whatever follows the amount belongs to
    // the next price, not to this one — taking it as a suffix gave "€7 €".
    if (prefix) suffix = '';
    return { prefix: prefix, suffix: suffix };
  }

  function formatMoney(amount, currency) {
    var out = String(Math.round(amount));
    if (!currency) return out;
    if (currency.prefix) {
      // Letters need a space ("SEK 15"), symbols don't ("$15").
      out = currency.prefix + (/[A-Za-z]/.test(currency.prefix) ? ' ' : '') + out;
    }
    if (currency.suffix) out += ' ' + currency.suffix;
    return out;
  }

  // Idempotent: re-reads whatever is in the DOM, so running it on our own
  // output is a no-op.
  function formatPrices(container) {
    var currency = detectCurrency(container);
    MONEY_SELECTORS.forEach(function (selector) {
      var els = container.querySelectorAll(selector);
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        if (el.getAttribute('bss-b2b-cart-price-active') === 'true') continue; // BSS B2B owns this value now
        if (el.querySelector('input, button, span')) continue; // e.g. discount field
        var amount = parseAmount(el.textContent);
        if (amount === null) continue;
        var out = formatMoney(amount, currency);
        if (el.textContent.trim() !== out) el.textContent = out;
      }
    });
  }

  /* ---------------- BSS B2B pricing ---------------- */

  // The summary is rendered by the CML app, so we can't add BSS attributes in
  // Liquid. Instead tag the app's price nodes the way BSS expects — line items
  // by their product/variant id, and the grand total — so the BSS B2B app
  // substitutes B2B prices here too. Inert for non-B2B customers; idempotent.
  function tagBssPricing(container) {
    var items = container.querySelectorAll('.cml_line_item');
    for (var i = 0; i < items.length; i++) {
      var priceEl = items[i].querySelector('.price');
      if (!priceEl || priceEl.hasAttribute('bss-b2b-variant-price')) continue;
      var pid = items[i].getAttribute('data-product-id');
      var vid = items[i].getAttribute('data-variant-id');
      if (!pid || !vid) continue;
      priceEl.setAttribute('bss-b2b-product-id', pid);
      priceEl.setAttribute('bss-b2b-variant-id', vid);
      priceEl.setAttribute('bss-b2b-variant-price', '');
    }
    var grand = container.querySelector('.grand_total .price');
    if (grand && !grand.hasAttribute('bss-b2b-cart-total-price')) {
      grand.setAttribute('bss-b2b-cart-total-price', '');
    }
  }

  // BSS matches a generic ".price" selector, so once B2B pricing is active it
  // also injects a product price span into the shipping / tax rows (which reuse
  // ".price" / ".price-row"). Those aren't products, so collapse each back to
  // its own first amount, dropping BSS's injected span. Line items and the grand
  // total are left alone — B2B pricing belongs there.
  function cleanSummaryRows(container) {
    var currency = detectCurrency(container);
    var rows = container.querySelectorAll('.shipping .price, .shipping-row .price-row, .tax .price');
    for (var i = 0; i < rows.length; i++) {
      var amount = parseAmount(rows[i].textContent);
      if (amount === null) continue;
      var out = formatMoney(amount, currency);
      if (rows[i].textContent !== out) rows[i].textContent = out;
    }
  }

  /* ---------------- grand total (BSS re-apply safety net) ---------------- */

  // BSS discounts the grand total, but the CML app re-renders the summary on
  // shipping changes and BSS doesn't re-apply, leaving the retail total. This is
  // value-based (doesn't trust BSS's active flag, which can go stale): remember
  // the discounted total verbatim whenever a genuine discount is on screen, and
  // restore it when the total snaps back to retail. Inert for non-B2B carts, and
  // the products don't change on a shipping switch, so the total is unchanged.
  var bssDiscountAmount = null;
  var bssTotalWritten = null;

  function grandRetailCents(el) {
    var n = parseInt(el.getAttribute('data-price') || el.getAttribute('data-price-fixed'), 10);
    return isNaN(n) ? null : n;
  }

  // Rebuild an amount using the separators and affixes of a reference string.
  function formatAmountLike(template, value) {
    var t = String(template).replace(/ /g, ' ');
    var m = t.match(/-?\d[\d\s.,]*/);
    if (!m) return null;
    var token = m[0].replace(/\s+$/, '');
    var stripped = token.replace(/\s/g, '');
    var decPos = Math.max(stripped.lastIndexOf(','), stripped.lastIndexOf('.'));
    var after = decPos === -1 ? 0 : stripped.length - decPos - 1;
    var decSep = after >= 1 && after <= 2 ? stripped.charAt(decPos) : '';
    // Any non-digit left in the number is a thousands separator (space, dot or
    // comma), once the decimal separator is excluded.
    var others = token.replace(/[\d-]/g, '').split('').filter(function (c) {
      return c !== decSep;
    });
    var thouSep = others.length ? others[0] : '';
    var parts = value.toFixed(decSep ? 2 : 0).split('.');
    var grouped = thouSep ? parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thouSep) : parts[0];
    var number = decSep && parts[1] ? grouped + decSep + parts[1] : grouped;
    return t.slice(0, m.index) + number + t.slice(m.index + token.length);
  }

  function syncGrandTotal(container) {
    var grand = container.querySelector('.grand_total .price');
    if (!grand) return;
    var retailCents = grandRetailCents(grand);
    if (!retailCents) return;
    var shown = parseAmount(grand.textContent);
    if (shown === null) return;
    var retail = retailCents / 100;

    // BSS wraps its own value in a span; anything we write is plain text. That
    // tells us whether the figure on screen is BSS's (trustworthy for learning
    // the discount) or the app's freshly rendered, undiscounted one.
    var writtenByBss = !!grand.querySelector('span');

    if (writtenByBss && shown < retail * 0.999) {
      bssDiscountAmount = retail - shown;
      bssTotalWritten = null;
      return;
    }

    if (!(bssDiscountAmount > 0) || writtenByBss) return;
    if (grand.textContent.trim() === bssTotalWritten) return; // already ours

    // Take the same amount off whatever the app now shows, instead of restoring
    // a remembered figure — that way shipping the app added on top is kept.
    var target = shown - bssDiscountAmount;
    if (!(target > 0)) return;
    var out = formatAmountLike(grand.textContent, target);
    if (out && grand.textContent.trim() !== out) {
      bssTotalWritten = out;
      grand.textContent = out;
    }
  }

  /* ---------------- primary button ---------------- */

  function findVisiblePrimary(container) {
    for (var i = 0; i < PRIMARY_IDS.length; i++) {
      var el = container.querySelector('#' + PRIMARY_IDS[i]);
      if (el && isVisible(el)) return el;
    }
    return null;
  }

  // Copy the app-styled #place_order input's look onto a relocated <a>, so
  // "Place Order" matches "Continue to shipping".
  function styleLinkLikeButton(container, link) {
    var ref = container.querySelector('#place_order');
    if (!ref) return;
    var cs = window.getComputedStyle(ref);
    var visual = {
      'background-color': cs.backgroundColor,
      'color': cs.color,
      'font-family': cs.fontFamily,
      'font-weight': cs.fontWeight,
      'letter-spacing': cs.letterSpacing,
      'text-transform': cs.textTransform,
      'border-radius': cs.borderTopLeftRadius,
      'padding-left': cs.paddingLeft,
      'padding-right': cs.paddingRight,
      'font-size': '13px', // keep in sync with the input rule in style_dev.css
      'height': '48px',
      'min-height': '48px',
      'padding-top': '0',
      'padding-bottom': '0',
      'border': 'none',
      'text-decoration': 'none'
    };
    for (var k in visual) {
      if (visual[k]) link.style.setProperty(k, visual[k], 'important');
    }
    link.style.display = 'inline-flex';
    link.style.alignItems = 'center';
    link.style.justifyContent = 'center';
    link.style.boxSizing = 'border-box';
    link.style.margin = '0';
  }

  function findBackLink(container) {
    var links = container.querySelectorAll('a, button, [role="button"]');
    for (var i = 0; i < links.length; i++) {
      var txt = (links[i].textContent || '').trim().toLowerCase();
      if (txt.indexOf(BACK_LINK_TEXT) !== -1) return links[i];
    }
    return null;
  }

  function relocatePrimaryButton(container) {
    var backLink = findBackLink(container);
    if (!backLink) return;

    var footer = backLink.closest('.' + FOOTER_CLASS);
    if (!footer) {
      footer = document.createElement('div');
      footer.className = FOOTER_CLASS;
      backLink.parentElement.insertBefore(footer, backLink);
      footer.appendChild(backLink);
    }

    var group = footer.querySelector('.' + ACTIONS_CLASS);
    if (!group) {
      group = document.createElement('div');
      group.className = ACTIONS_CLASS;
      footer.appendChild(group);
    }

    var button = findVisiblePrimary(container);
    if (!button) return;
    if (button.parentElement !== group) group.appendChild(button);
    if (button.tagName === 'A') styleLinkLikeButton(container, button);
  }

  /* ---------------- summary + form tweaks ---------------- */

  // Rename the app's "Shipping (Method)" summary label, keeping the method span
  // intact (cleanupShippingLabel below depends on it).
  function relabelShipping(container) {
    var names = container.querySelectorAll('.shipping_name');
    for (var i = 0; i < names.length; i++) {
      var nodes = names[i].childNodes;
      for (var j = 0; j < nodes.length; j++) {
        var node = nodes[j];
        if (node.nodeType === 3 && node.nodeValue.indexOf('Shipping') !== -1) {
          node.nodeValue = node.nodeValue.replace('Shipping', SHIPPING_LABEL);
        }
      }
    }
  }

  // Add the same label just left of the price in the shipping rate selector.
  function labelShippingRow(container) {
    var rows = container.querySelectorAll('.shipping-row');
    for (var i = 0; i < rows.length; i++) {
      var price = rows[i].querySelector('.price-row');
      if (!price || rows[i].querySelector('.mg-rate-label')) continue;
      var label = document.createElement('span');
      label.className = 'mg-rate-label';
      label.textContent = SHIPPING_LABEL;
      price.parentElement.insertBefore(label, price);
    }
  }

  // The app renders `Shipping (<span>method</span>)`; before a rate is chosen
  // the inner span is empty, leaving "Shipping ()". Hide the row until then.
  function cleanupShippingLabel(container) {
    var names = container.querySelectorAll('.shipping_name');
    for (var i = 0; i < names.length; i++) {
      var nameEl = names[i];
      var inner = nameEl.querySelector('span');
      var hasMethod = inner && inner.textContent.trim() !== '';
      var line = nameEl.closest('li.shipping') || nameEl.closest('li') || nameEl;
      line.style.display = hasMethod ? '' : 'none';
    }
  }

  // First/Last, Email, Phone, Address, Zip, then City + Country as two columns.
  function reorderAddressForm(container) {
    var w = container.querySelector('#shipping_address_widget');
    if (!w || w.getAttribute('data-mg-reordered') === '1') return;

    var namesBox = w.querySelector('.full-box');
    var email = w.querySelector('input[name="email"]');
    var address1 = w.querySelector('#address1_cml');
    var city = w.querySelector('#city_cml');
    var country = w.querySelector('#AddressCountry');
    var countryErr = w.querySelector('p.error.country');
    var zip = w.querySelector('#zipCode');
    var zipErr = w.querySelector('p.error.zipcode');
    var phone = w.querySelector('input[name="address[phone]"]');

    if (!namesBox || !email || !address1 || !city || !country || !zip || !phone) return;

    var row = document.createElement('div');
    row.className = 'full-box mg-city-country-row';
    var cityCol = document.createElement('div');
    cityCol.className = 'half-box';
    var countryCol = document.createElement('div');
    countryCol.className = 'half-box';
    cityCol.appendChild(city);
    countryCol.appendChild(country);
    if (countryErr) countryCol.appendChild(countryErr);
    row.appendChild(cityCol);
    row.appendChild(countryCol);

    var frag = document.createDocumentFragment();
    frag.appendChild(email);
    frag.appendChild(phone);
    frag.appendChild(address1);
    frag.appendChild(zip);
    if (zipErr) frag.appendChild(zipErr);
    frag.appendChild(row);
    namesBox.after(frag);

    w.setAttribute('data-mg-reordered', '1');
  }

  function apply() {
    var container = document.querySelector(CONTAINER_SELECTOR);
    if (!container || applying) return;
    applying = true;
    try {
      reorderAddressForm(container);
      relabelShipping(container);
      labelShippingRow(container);
      cleanupShippingLabel(container);
      relocatePrimaryButton(container);
      tagBssPricing(container);
      formatPrices(container);
      cleanSummaryRows(container);
      syncGrandTotal(container);
    } finally {
      applying = false;
    }
  }

  function init() {
    apply();
    var start = Date.now();
    var observer = new MutationObserver(function () {
      apply();
      if (Date.now() - start > MAX_WAIT_MS) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    setTimeout(function () {
      observer.disconnect();
    }, MAX_WAIT_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
